import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useBetty } from '../../context/BettyContext';
import { useVoiceInput } from '../../hooks/useVoiceInput';
import { AudioWaveform } from './AudioWaveform';

const InputContainer = styled.div`
  padding: 20px;
  background: rgba(255, 248, 240, 0.95);
  border-top: 1px solid rgba(255, 105, 180, 0.2);
  position: relative;
  z-index: 2;
`;

const InputWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
`;

const InputForm = styled.form`
  display: flex;
  gap: 12px;
  align-items: flex-end;
`;

const TextInput = styled.textarea`
  flex: 1;
  min-height: 50px;
  max-height: 120px;
  padding: 16px 20px;
  border: 1px solid rgba(255, 105, 180, 0.3);
  border-radius: 25px;
  background: rgba(255, 255, 255, 0.9);
  color: #8b4513;
  font-size: 1rem;
  font-family: inherit;
  resize: none;
  outline: none;
  transition: all 0.3s ease;

  &::placeholder {
    color: rgba(139, 69, 19, 0.5);
  }

  &:focus {
    border-color: #ff69b4;
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 0 0 2px rgba(255, 105, 180, 0.2);
  }

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 182, 193, 0.2);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 105, 180, 0.5);
    border-radius: 3px;
    
    &:hover {
      background: rgba(255, 105, 180, 0.7);
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const SendButton = styled.button<{ disabled: boolean }>`
  width: 50px;
  height: 50px;
  border: none;
  border-radius: 50%;
  background: ${props => props.disabled
    ? 'rgba(255, 105, 180, 0.3)'
    : 'linear-gradient(135deg, #ff69b4, #ffb6c1)'
  };
  color: ${props => props.disabled ? 'rgba(139, 69, 19, 0.5)' : 'white'};
  font-size: 1.2rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(255, 105, 180, 0.3);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }
`;

const VoiceButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'isRecording' && prop !== 'error'
}) <{
  isRecording: boolean;
  disabled: boolean;
  error?: boolean;
}>`
  width: 50px;
  height: 50px;
  border: none;
  border-radius: 50%;
  background: ${props => {
    if (props.error) return 'linear-gradient(135deg, #ff6b6b, #ff8e8e)';
    if (props.isRecording) return 'linear-gradient(135deg, #ff4757, #ff6b6b)';
    return props.disabled
      ? 'rgba(255, 105, 180, 0.3)'
      : 'linear-gradient(135deg, #ff69b4, #ffb6c1)';
  }};
  color: ${props => props.disabled ? 'rgba(139, 69, 19, 0.5)' : 'white'};
  font-size: 1.2rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 4px 12px rgba(255, 105, 180, 0.3);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }
`;

const VoiceControls = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-height: 60px;
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  font-size: 0.8rem;
  text-align: center;
  max-width: 200px;
`;

const TypingIndicator = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible'
}) <{ visible: boolean }>`
  position: absolute;
  top: -40px;
  left: 20px;
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.3s ease;
  color: rgba(139, 69, 19, 0.7);
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 8px;

  .dots {
    display: flex;
    gap: 2px;
  }

  .dot {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: #ff69b4;
    animation: typingDots 1.4s infinite ease-in-out;
    
    &:nth-child(1) { animation-delay: 0s; }
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }

  @keyframes typingDots {
    0%, 60%, 100% {
      transform: scale(1);
      opacity: 0.5;
    }
    30% {
      transform: scale(1.2);
      opacity: 1;
    }
  }
`;

const CharCount = styled.div<{ warning: boolean }>`
  position: absolute;
  bottom: -25px;
  right: 0;
  font-size: 0.8rem;
  color: ${props => props.warning ? '#ff6b6b' : 'rgba(244, 228, 193, 0.5)'};
  transition: color 0.3s ease;
`;

const MAX_LENGTH = 500;

export const BettyChatInput: React.FC = () => {
  const [message, setMessage] = useState('');
  const { sendMessage, isLoading } = useBetty();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const {
    isRecording,
    isTranscribing,
    audioLevel,
    startRecording,
    stopRecording,
    error: voiceError
  } = useVoiceInput();

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [message]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || isLoading) return;

    const messageToSend = message.trim();
    setMessage('');

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    await sendMessage(messageToSend);
  };

  const handleVoiceToggle = async () => {
    if (isRecording) {
      // Stop recording and transcribe
      const transcribedText = await stopRecording();
      if (transcribedText) {
        setMessage(transcribedText);
        // Auto-submit the transcribed text
        setTimeout(async () => {
          await sendMessage(transcribedText);
        }, 100);
      }
    } else {
      // Start recording
      setMessage(''); // Clear current message
      await startRecording();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const canSend = message.trim().length > 0 && !isLoading && message.length <= MAX_LENGTH;
  const isNearLimit = message.length > MAX_LENGTH * 0.8;
  const canUseVoice = !isLoading && !isTranscribing;

  return (
    <InputContainer>
      <InputWrapper>
        <TypingIndicator visible={isLoading}>
          Betty is thinking
          <div className="dots">
            <div className="dot"></div>
            <div className="dot"></div>
            <div className="dot"></div>
          </div>
        </TypingIndicator>

        <InputForm onSubmit={handleSubmit}>
          <TextInput
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask Betty about vintage music for your project..."
            disabled={isLoading || isTranscribing}
            maxLength={MAX_LENGTH}
          />
          <ButtonGroup>
            <VoiceButton
              type="button"
              onClick={handleVoiceToggle}
              disabled={!canUseVoice}
              isRecording={isRecording}
              error={!!voiceError}
              title={
                voiceError
                  ? `Voice error: ${voiceError}`
                  : isRecording
                    ? 'Click to stop recording'
                    : 'Click to start voice recording'
              }
            >
              {isRecording ? '⏹' : '🎤'}
            </VoiceButton>
            <SendButton
              type="submit"
              disabled={!canSend}
              title={canSend ? 'Send message' : 'Type a message to send'}
            >
              ➤
            </SendButton>
          </ButtonGroup>
        </InputForm>

        {(isRecording || isTranscribing) && (
          <VoiceControls>
            <AudioWaveform
              isRecording={isRecording}
              audioLevel={audioLevel}
              isTranscribing={isTranscribing}
            />
            {voiceError && (
              <ErrorMessage>
                {voiceError.includes('quota') || voiceError.includes('billing')
                  ? 'Voice features temporarily unavailable due to API quota limits. Please try again later or use text input.'
                  : voiceError
                }
              </ErrorMessage>
            )}

            {isRecording && (
              <div style={{ fontSize: '0.8rem', color: 'rgba(139, 69, 19, 0.7)', textAlign: 'center', marginTop: '4px' }}>
                Recording... (auto-stops at 30s to control costs)
              </div>
            )}

            {isTranscribing && (
              <div style={{ fontSize: '0.8rem', color: 'rgba(139, 69, 19, 0.7)', textAlign: 'center', marginTop: '4px' }}>
                Processing audio and transcribing...
              </div>
            )}
          </VoiceControls>
        )}

        {(isNearLimit || message.length > MAX_LENGTH) && (
          <CharCount warning={message.length > MAX_LENGTH}>
            {message.length}/{MAX_LENGTH}
          </CharCount>
        )}
      </InputWrapper>
    </InputContainer>
  );
};